/**
 * /demo intake rules (spec A4 + handoff §4). Pure, so the button's enabled
 * state and what gets priced are decided in one tested place.
 *
 * @module web/screens/demo/intake
 */

export const MIN_BRIEF_CHARS = 10;
export const STORAGE_KEY = "aditor.demo.v1";

export interface IntakeState {
  brief: string;
  /** What the visitor typed or Jev found. */
  productUrl: string;
  /** "No product page" was tapped. */
  noProduct: boolean;
}

/** Postel: `grindco.com/x` and `www.grindco.com` are URLs too. */
export function normalizeUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const withScheme = /^https?:\/\//i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(withScheme);
    return /\.[a-z]{2,}$/i.test(u.hostname) ? u.toString() : null;
  } catch {
    return null;
  }
}

export function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

export function briefReady(s: IntakeState): boolean {
  return s.brief.trim().length >= MIN_BRIEF_CHARS;
}

export function canQuote(s: IntakeState): boolean {
  return briefReady(s) && (s.noProduct || normalizeUrl(s.productUrl) !== null);
}

/**
 * What gets priced — and what the quote token binds, so B1 must send the same
 * string back. The product page travels beside it, never inside: the server
 * would try to read it, and a slow shop page must not sink the price.
 */
export function quoteRequest(s: IntakeState): { raw: string; productUrl?: string } {
  const url = s.noProduct ? null : normalizeUrl(s.productUrl);
  return url ? { raw: s.brief.trim(), productUrl: url } : { raw: s.brief.trim() };
}

/** Counts the browser can make on its own before the quote is back. */
export function localCounts(brief: string): { lines: number; words: number } {
  const lines = brief.split(/\n/).filter((l) => l.trim() !== "").length;
  const words = (brief.match(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu) ?? []).length;
  return { lines, words };
}
